import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { useDebounce } from 'react-use'
import isEmpty from 'lodash/isEmpty'
import uniq from 'lodash/uniq'
import flatMap from 'lodash/flatMap'
import { gql } from '@apollo/client'
import { Overlay, Popover, ListGroup, Stack, ButtonGroup, Form } from 'react-bootstrap'
import type { DateNameEnum } from '@ztechapps/common.utils'
import type { TaskFilterDateRange, FilterOption } from '@ztechapps/task.common'
import { UNASSIGNED_FILTER_ID } from '@ztechapps/task.common'
import { useLazyList } from '@ztechapps/common.hook'
import { BusyButton } from '@ztechapps/webui.button'
import { HorizontalStack, Icon, ShowIf, Spinner } from '@ztechapps/webui.util'
import { DateFilter } from '@ztechapps/webui.data-tools'
import { useTaskFilterBarContext } from './Context'
import styles from './TaskFilterBar.module.scss'

// ─── GraphQL Queries ────────────────────────────────────────────────

const projectSearchQuery = gql`
  query allProject($filter: JSON!, $options: JSON) {
    allProject(filter: $filter, options: $options) {
      _id
      code
      name
      status
      relatedWorkOrders {
        _id
      }
    }
  }
`

const workOrderSearchQuery = gql`
  query allWorkOrder($filter: JSON!, $options: JSON) {
    allWorkOrder(filter: $filter, options: $options) {
      _id
      code
      status
      product {
        _id
        code
      }
    }
  }
`

const collaboratorUsersQuery = gql`
  query getCollaboratorUsers($filter: JSON!, $options: JSON) {
    getCollaboratorUsers(filter: $filter, options: $options) {
      _id
      profile {
        _id
        name
        email
      }
      status
    }
  }
`

// ─── Searchable Checkbox Popover (Option C) ─────────────────────────

type SearchableCheckboxPopoverProps = {
  show: boolean
  target: React.RefObject<HTMLElement>
  title: string
  selectedItems: FilterOption[]
  searchResults: FilterOption[]
  searchLoading: boolean
  onSearch: (term: string) => void
  onApply: (ids: string[], items: FilterOption[]) => void
  onReset: () => void
  onClose: () => void
  searchPlaceholder?: string
}

const SearchableCheckboxPopover = ({
  show,
  target,
  title,
  selectedItems,
  searchResults,
  searchLoading,
  onSearch,
  onApply,
  onReset,
  onClose,
  searchPlaceholder = 'Type to search...',
}: SearchableCheckboxPopoverProps) => {
  const [localSelected, setLocalSelected] = useState<FilterOption[]>(selectedItems)
  const [searchTerm, setSearchTerm] = useState('')

  // Sync local state when popover opens
  useEffect(() => {
    if (show) {
      setLocalSelected(selectedItems)
      setSearchTerm('')
      onSearch('')
    }
    // Intentionally depend only on `show` — re-sync local state from props when popover opens/closes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  // Debounced search
  useDebounce(
    () => {
      onSearch(searchTerm)
    },
    300,
    [searchTerm]
  )

  const localSelectedIds = useMemo(() => localSelected.map((i) => i._id), [localSelected])

  const handleToggle = useCallback((item: FilterOption) => {
    setLocalSelected((prev) => {
      const exists = prev.some((s) => s._id === item._id)
      if (exists) {
        return prev.filter((s) => s._id !== item._id)
      }
      return [...prev, item]
    })
  }, [])

  const handleApply = () => {
    onApply(
      localSelected.map((i) => i._id),
      localSelected
    )
    onClose()
  }

  const handleReset = () => {
    onReset()
    onClose()
  }

  // Filter out already-selected items from search results
  const filteredResults = useMemo(() => {
    return searchResults.filter((r) => !localSelectedIds.includes(r._id))
  }, [searchResults, localSelectedIds])

  return (
    <Overlay show={show} target={target.current} placement="bottom-start" rootClose onHide={onClose}>
      <Popover id={`filter-chip-${title}`} className="fw-normal fs-5 shadow-lg">
        <Popover.Header className="fw-semibold">{title}</Popover.Header>
        <Popover.Body className={styles.chipPopoverBody}>
          <Stack gap={0}>
            {/* Search Input */}
            <div className="p-2 border-bottom">
              <Form.Control
                type="text"
                size="sm"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>

            {/* Selected Items */}
            <ShowIf condition={!isEmpty(localSelected)}>
              <div className="px-2 pt-2 pb-1">
                <small className="text-muted fw-semibold">Selected</small>
              </div>
              <ListGroup variant="flush">
                {localSelected.map((item) => (
                  <ListGroup.Item
                    key={item._id}
                    action
                    active
                    onClick={() => handleToggle(item)}
                    className="py-2 d-flex align-items-center gap-2"
                  >
                    <Icon name="check" />
                    {item.label}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </ShowIf>

            {/* Search Results */}
            <div className={styles.chipPopoverResults}>
              <ShowIf condition={searchLoading}>
                <div className="p-3 text-center text-muted">
                  <Spinner size="sm" /> Searching...
                </div>
              </ShowIf>
              <ShowIf condition={!searchLoading && !isEmpty(filteredResults)}>
                <ShowIf condition={!isEmpty(localSelected)}>
                  <div className="px-2 pt-2 pb-1">
                    <small className="text-muted fw-semibold">Results</small>
                  </div>
                </ShowIf>
                <ListGroup variant="flush">
                  {filteredResults.map((item) => (
                    <ListGroup.Item key={item._id} action onClick={() => handleToggle(item)} className="py-2">
                      {item.label}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </ShowIf>
              <ShowIf condition={!searchLoading && isEmpty(filteredResults) && !isEmpty(searchTerm)}>
                <div className="p-3 text-center text-muted small">No results found</div>
              </ShowIf>
            </div>

            {/* Reset / Filter / Cancel */}
            <HorizontalStack className="justify-content-between border-top p-2">
              <BusyButton text="Reset" bsSize="sm" variant="warning" onClick={handleReset} />
              <ButtonGroup>
                <BusyButton text="Filter" bsSize="sm" variant="primary" onClick={handleApply} />
                <BusyButton text="Cancel" bsSize="sm" variant="secondary" onClick={onClose} />
              </ButtonGroup>
            </HorizontalStack>
          </Stack>
        </Popover.Body>
      </Popover>
    </Overlay>
  )
}

// ─── Due Date Popover (reuses DateFilter from DataTable) ────────────

type DueDatePopoverProps = {
  show: boolean
  target: React.RefObject<HTMLElement>
  dateRange: TaskFilterDateRange
  onApply: (dateRange: TaskFilterDateRange) => void
  onReset: () => void
  onClose: () => void
}

const DueDatePopover = ({ show, target, dateRange, onApply, onReset, onClose }: DueDatePopoverProps) => {
  const [localPreset, setLocalPreset] = useState<DateNameEnum | null>(dateRange.presetName ?? null)
  const [localStart, setLocalStart] = useState<string>(dateRange.customStart ?? '')
  const [localEnd, setLocalEnd] = useState<string>(dateRange.customEnd ?? '')

  useEffect(() => {
    if (show) {
      setLocalPreset(dateRange.presetName ?? null)
      setLocalStart(dateRange.customStart ?? '')
      setLocalEnd(dateRange.customEnd ?? '')
    }
    // Intentionally depend only on `show` — re-sync local state from props when popover opens/closes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const handlePresetClick = (preset: DateNameEnum) => {
    setLocalPreset(preset)
    setLocalStart('')
    setLocalEnd('')
    // Apply immediately like DataTable does
    onApply({ presetName: preset })
    onClose()
  }

  const handleStartChange = (date: string) => {
    setLocalStart(date)
    setLocalPreset(null)
  }

  const handleEndChange = (date: string) => {
    setLocalEnd(date)
    setLocalPreset(null)
  }

  const handleCustomApply = () => {
    if (localStart || localEnd) {
      onApply({ customStart: localStart || undefined, customEnd: localEnd || undefined })
      onClose()
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleCustomApply()
    }
  }

  const handleReset = () => {
    onReset()
    onClose()
  }

  return (
    <Overlay show={show} target={target.current} placement="bottom-start" rootClose onHide={onClose}>
      <Popover id="filter-chip-due-date" className="fw-normal fs-5 shadow-lg">
        <Popover.Header className="fw-semibold">Due Date</Popover.Header>
        <Popover.Body className={styles.datePopoverBody}>
          <DateFilter
            isActive={(current) => current === localPreset}
            onClickListItem={handlePresetClick}
            startDate={localStart}
            endDate={localEnd}
            onStartDateChange={handleStartChange}
            onEndDateChange={handleEndChange}
            onKeyPress={handleKeyPress}
          />
          <HorizontalStack className="justify-content-between border-top p-2">
            <BusyButton text="Reset" bsSize="sm" variant="warning" onClick={handleReset} />
            <ShowIf condition={!localPreset && !!(localStart || localEnd)}>
              <BusyButton text="Apply Custom Range" bsSize="sm" variant="primary" onClick={handleCustomApply} />
            </ShowIf>
          </HorizontalStack>
        </Popover.Body>
      </Popover>
    </Overlay>
  )
}

// ─── Filter Chip Button ─────────────────────────────────────────────

type FilterChipButtonProps = {
  label: string
  isActive: boolean
  chipRef: React.RefObject<HTMLButtonElement>
  onClick: () => void
}

const FilterChipButton = ({ label, isActive, chipRef, onClick }: FilterChipButtonProps) => (
  <button
    type="button"
    ref={chipRef}
    className={`${styles.filterChip} d-inline-flex align-items-center gap-1 px-2 py-1 border rounded-pill fw-medium text-nowrap ${isActive ? styles.filterChipActive : 'bg-transparent text-secondary'}`}
    onClick={onClick}
  >
    <Icon name={isActive ? 'filter' : 'plus'} className="me-1" />
    {label}
  </button>
)

// ─── Filter item types for lazy search queries ─────────────────────

type ProjectFilterItem = {
  _id: string
  code: string
  name: string
  status: string
  relatedWorkOrders: { _id: string }[]
}
type WorkOrderFilterItem = { _id: string; code: string; status: string; product?: { _id?: string; code?: string } }
type UserFilterItem = { _id: string; profile?: { _id?: string; name?: string; email?: string }; status: string }

// ─── Main TaskFilterChips ────────────────────────────────────────────

export const TaskFilterChips = () => {
  const { filterState, setFilter, clearFilter, projectList } = useTaskFilterBarContext()

  // Open state for each chip popover
  const [openChip, setOpenChip] = useState<string | null>(null)

  // Refs for popover targets
  const projectRef = useRef<HTMLButtonElement>(null)
  const workOrderRef = useRef<HTMLButtonElement>(null)
  const assigneeRef = useRef<HTMLButtonElement>(null)
  const dateRef = useRef<HTMLButtonElement>(null)

  // ─── Lazy search queries ──────────────────────────────────────────

  const {
    list: projectSearchResults,
    loading: projectSearchLoading,
    executeQuery: searchProjects,
  } = useLazyList<ProjectFilterItem>({
    query: projectSearchQuery,
    entity: 'allProject',
  })

  const {
    list: woSearchResults,
    loading: woSearchLoading,
    executeQuery: searchWorkOrders,
  } = useLazyList<WorkOrderFilterItem>({
    query: workOrderSearchQuery,
    entity: 'allWorkOrder',
  })

  const {
    list: assigneeSearchResults,
    loading: assigneeSearchLoading,
    executeQuery: searchAssignees,
  } = useLazyList<UserFilterItem>({
    query: collaboratorUsersQuery,
    entity: 'getCollaboratorUsers',
  })

  // Compute available work order IDs from selected projects
  const workOrderFilterFromProjects = useMemo(() => {
    if (isEmpty(filterState.projectIds)) return {}

    const projects = projectList?.list ?? []
    const selectedProjects = projects.filter((p) => filterState.projectIds.includes(p._id))
    const relatedWoIds = uniq(
      flatMap(selectedProjects, (p) => (p.relatedWorkOrders ?? []).map((wo: { _id: string }) => wo._id))
    )

    if (isEmpty(relatedWoIds)) {
      return { _id: { $in: [] } }
    }

    return { _id: { $in: relatedWoIds } }
  }, [filterState.projectIds, projectList?.list])

  // Reverse mapping: compute available project IDs from selected work orders
  const projectFilterFromWorkOrders = useMemo(() => {
    if (isEmpty(filterState.workOrderIds)) return {}

    const projects = projectList?.list ?? []
    const matchingProjectIds = projects
      .filter((p) => (p.relatedWorkOrders ?? []).some((wo) => filterState.workOrderIds.includes(wo._id)))
      .map((p) => p._id)

    if (isEmpty(matchingProjectIds)) {
      return { _id: { $in: [] } }
    }

    return { _id: { $in: matchingProjectIds } }
  }, [filterState.workOrderIds, projectList?.list])

  // ─── Search handlers ──────────────────────────────────────────────

  const handleProjectSearch = useCallback(
    (term: string) => {
      const baseFilter = isEmpty(term)
        ? { ...projectFilterFromWorkOrders }
        : {
            $or: [{ code: { $regex: term, $options: 'i' } }, { name: { $regex: term, $options: 'i' } }],
            ...projectFilterFromWorkOrders,
          }
      searchProjects({ variables: { filter: baseFilter, options: { sort: { createdAt: -1 }, limit: 50 } } })
    },
    [searchProjects, projectFilterFromWorkOrders]
  )

  const handleWorkOrderSearch = useCallback(
    (term: string) => {
      const baseFilter = isEmpty(term)
        ? { ...workOrderFilterFromProjects }
        : { $or: [{ code: { $regex: term, $options: 'i' } }], ...workOrderFilterFromProjects }
      searchWorkOrders({ variables: { filter: baseFilter, options: { sort: { createdAt: -1 }, limit: 50 } } })
    },
    [searchWorkOrders, workOrderFilterFromProjects]
  )

  const handleAssigneeSearch = useCallback(
    (term: string) => {
      const filter = isEmpty(term)
        ? { status: 'active' }
        : {
            status: 'active',
            $or: [
              { 'profile.name': { $regex: term, $options: 'i' } },
              { 'profile.email': { $regex: term, $options: 'i' } },
            ],
          }
      searchAssignees({ variables: { filter, options: { sort: { 'profile.name': 1 }, limit: 50 } } })
    },
    [searchAssignees]
  )

  // ─── Map search results to FilterOption ───────────────────────────

  const projectOptions = useMemo<FilterOption[]>(() => {
    return (projectSearchResults ?? []).map((p: ProjectFilterItem) => ({
      _id: p._id,
      label: `${p.code} - ${p.name}`,
    }))
  }, [projectSearchResults])

  const workOrderOptions = useMemo<FilterOption[]>(() => {
    return (woSearchResults ?? []).map((wo: WorkOrderFilterItem) => ({
      _id: wo._id,
      label: wo.product?.code ? `${wo.code} - ${wo.product.code}` : wo.code,
    }))
  }, [woSearchResults])

  const assigneeOptions = useMemo<FilterOption[]>(() => {
    const users = (assigneeSearchResults ?? []).map((u: UserFilterItem) => ({
      _id: u._id,
      label: u.profile?.name ?? u.profile?.email ?? u._id,
    }))
    return [{ _id: UNASSIGNED_FILTER_ID, label: 'Unassigned' }, ...users]
  }, [assigneeSearchResults])

  // ─── Popover toggle ───────────────────────────────────────────────

  const toggleChip = useCallback((chip: string) => {
    setOpenChip((prev) => (prev === chip ? null : chip))
  }, [])

  const closeChip = useCallback(() => {
    setOpenChip(null)
  }, [])

  // ─── Apply handlers ───────────────────────────────────────────────

  const handleProjectApply = useCallback(
    (ids: string[], items: FilterOption[]) => {
      setFilter({ projectIds: ids, selectedProjectItems: items })
    },
    [setFilter]
  )

  const handleWorkOrderApply = useCallback(
    (ids: string[], items: FilterOption[]) => {
      setFilter({ workOrderIds: ids, selectedWorkOrderItems: items })
    },
    [setFilter]
  )

  const handleAssigneeApply = useCallback(
    (ids: string[], items: FilterOption[]) => {
      setFilter({ assigneeIds: ids, selectedAssigneeItems: items })
    },
    [setFilter]
  )

  const handleDateApply = useCallback(
    (dateRange: TaskFilterDateRange) => {
      setFilter({ dateRange })
    },
    [setFilter]
  )

  const isDueDateActive = !!(
    filterState.dateRange.presetName ??
    filterState.dateRange.customStart ??
    filterState.dateRange.customEnd
  )

  return (
    <HorizontalStack gap={2} className="flex-wrap align-items-center">
      {/* Project Chip */}
      <FilterChipButton
        label="Project"
        isActive={!isEmpty(filterState.projectIds)}
        chipRef={projectRef}
        onClick={() => toggleChip('project')}
      />
      <SearchableCheckboxPopover
        show={openChip === 'project'}
        target={projectRef}
        title="Filter by Project"
        selectedItems={filterState.selectedProjectItems ?? []}
        searchResults={projectOptions}
        searchLoading={projectSearchLoading}
        onSearch={handleProjectSearch}
        onApply={handleProjectApply}
        onReset={() => {
          setFilter({ projectIds: [], selectedProjectItems: [] })
        }}
        onClose={closeChip}
        searchPlaceholder="Search projects..."
      />

      {/* Work Order Chip */}
      <FilterChipButton
        label="Production"
        isActive={!isEmpty(filterState.workOrderIds)}
        chipRef={workOrderRef}
        onClick={() => toggleChip('workOrder')}
      />
      <SearchableCheckboxPopover
        show={openChip === 'workOrder'}
        target={workOrderRef}
        title="Filter by Production"
        selectedItems={filterState.selectedWorkOrderItems ?? []}
        searchResults={workOrderOptions}
        searchLoading={woSearchLoading}
        onSearch={handleWorkOrderSearch}
        onApply={handleWorkOrderApply}
        onReset={() => {
          setFilter({ workOrderIds: [], selectedWorkOrderItems: [] })
        }}
        onClose={closeChip}
        searchPlaceholder="Search production..."
      />

      {/* Assignee Chip */}
      <FilterChipButton
        label="Assignee"
        isActive={!isEmpty(filterState.assigneeIds)}
        chipRef={assigneeRef}
        onClick={() => toggleChip('assignee')}
      />
      <SearchableCheckboxPopover
        show={openChip === 'assignee'}
        target={assigneeRef}
        title="Filter by Assignee"
        selectedItems={filterState.selectedAssigneeItems ?? []}
        searchResults={assigneeOptions}
        searchLoading={assigneeSearchLoading}
        onSearch={handleAssigneeSearch}
        onApply={handleAssigneeApply}
        onReset={() => {
          setFilter({ assigneeIds: [], selectedAssigneeItems: [] })
        }}
        onClose={closeChip}
        searchPlaceholder="Search users..."
      />

      {/* Due Date Chip */}
      <FilterChipButton
        label="Due Date"
        isActive={isDueDateActive}
        chipRef={dateRef}
        onClick={() => toggleChip('dateRange')}
      />
      <DueDatePopover
        show={openChip === 'dateRange'}
        target={dateRef}
        dateRange={filterState.dateRange}
        onApply={handleDateApply}
        onReset={() => clearFilter('dateRange')}
        onClose={closeChip}
      />
    </HorizontalStack>
  )
}
